/**
 * API Configuration
 * Centralized configuration for all API endpoints
 */

// Get API URL from environment variable or use default
export const API_BASE_URL = import.meta.env.VITE_API_URL || '/api/v1';

// API Endpoints
export const API_ENDPOINTS = {
  // Auth
  auth: {
    register: `${API_BASE_URL}/auth/register`,
    login: `${API_BASE_URL}/auth/login`,
    logout: `${API_BASE_URL}/auth/logout`,
    me: `${API_BASE_URL}/auth/me`,
    session: `${API_BASE_URL}/auth/session`,
    google: `${API_BASE_URL}/auth/google`,
    googleCallback: `${API_BASE_URL}/auth/google/callback`,
    apple: `${API_BASE_URL}/auth/apple`,
    verifyEmail: `${API_BASE_URL}/auth/verify-email`,
    resendVerification: `${API_BASE_URL}/auth/resend-verification`,
    forgotPassword: `${API_BASE_URL}/auth/forgot-password`,
    resetPassword: `${API_BASE_URL}/auth/reset-password`,
    autoEnroll: `${API_BASE_URL}/auth/auto-enroll`,
    upgrade: `${API_BASE_URL}/auth/upgrade`,
    requestOtp: `${API_BASE_URL}/auth/request-otp`,
    verifyOtp: `${API_BASE_URL}/auth/verify-otp`,
  },

  // Geo (auto-detect for the respondent profile)
  geo: {
    detect: `${API_BASE_URL}/geo/detect`,
  },
  
  // Surveys
  surveys: {
    base: `${API_BASE_URL}/surveys`,
    byId: (id: string) => `${API_BASE_URL}/surveys/${id}`,
    public: (publicId: string) => `${API_BASE_URL}/surveys/public/${publicId}`,
    questions: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/questions`,
    respond: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/respond`,
    claimResponse: (responseId: string) => `${API_BASE_URL}/surveys/responses/${responseId}/claim`,
    responses: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/responses`,
    responsesExportCsv: (surveyId: string, lang: string) =>
      `${API_BASE_URL}/surveys/${surveyId}/responses/export.csv?lang=${lang}`,
    myResponses: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/my-responses`,
    myResponseById: (surveyId: string, responseId: string) =>
      `${API_BASE_URL}/surveys/${surveyId}/my-responses/${responseId}`,
    preview: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/preview`,
    compliance: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/compliance`,
    respondentHistory: (surveyId: string, email: string) => `${API_BASE_URL}/surveys/${surveyId}/respondent/${encodeURIComponent(email)}/history`,
    canAttempt: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/can-attempt`,
    translate: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/translate`,
    translationByLang: (surveyId: string, lang: string) => `${API_BASE_URL}/surveys/${surveyId}/translations/${lang}`,
  },

  // Organizations
  organizations: {
    base: `${API_BASE_URL}/organizations`,
    byId: (id: string) => `${API_BASE_URL}/organizations/${id}`,
    members: (id: string) => `${API_BASE_URL}/organizations/${id}/members`,
    membersExportCsv: (id: string, lang: string) =>
      `${API_BASE_URL}/organizations/${id}/members/export.csv?lang=${lang}`,
  },
  
  // Notifications
  notifications: {
    preferences: `${API_BASE_URL}/notifications/preferences`,
    muteSurvey: (id: string) => `${API_BASE_URL}/notifications/preferences/mute-survey/${id}`,
    unmuteSurvey: (id: string) => `${API_BASE_URL}/notifications/preferences/unmute-survey/${id}`,
    unsubscribe: `${API_BASE_URL}/notifications/unsubscribe`,
  },

  // Impact
  impact: {
    report: (surveyId: string) => `${API_BASE_URL}/surveys/${surveyId}/impact`,
    exportCsv: (surveyId: string, lang: string) => `${API_BASE_URL}/surveys/${surveyId}/export.csv?lang=${lang}`,
    exportPdf: (surveyId: string, lang: string) => `${API_BASE_URL}/surveys/${surveyId}/report.pdf?lang=${lang}`,
  },

  // Templates
  templates: {
    base: `${API_BASE_URL}/templates`,
    byId: (id: string) => `${API_BASE_URL}/templates/${id}`,
    createSurvey: (id: string) => `${API_BASE_URL}/templates/${id}/create-survey`,
  },

  // Sessions
  sessions: {
    base: `${API_BASE_URL}/sessions`,
    byId: (id: string) => `${API_BASE_URL}/sessions/${id}`,
    bySurvey: (surveyId: string) => `${API_BASE_URL}/sessions/survey/${surveyId}`,
  },
};

export default API_ENDPOINTS;